import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { AttendanceManager } from '@/components/instructor/AttendanceManager';
import { InstructorCommunication } from '@/components/instructor/InstructorCommunication';
import { Users, MessageSquare, Calendar, BookOpen, Trash2, Edit, Play, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { CompletionManager } from '@/components/instructor/CompletionManager';

interface Course {
  id: string;
  title: string;
  description: string;
  status: string;
  max_participants: number;
  duration_hours: number;
}

interface EnrollmentStats {
  total: number;
  enrolled: number;
  completed: number;
  waitlisted: number;
}

export default function CourseManagement() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [stats, setStats] = useState<EnrollmentStats>({
    total: 0,
    enrolled: 0,
    completed: 0,
    waitlisted: 0
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (courseId) {
      fetchCourseData();
      fetchEnrollmentStats();
    }
  }, [courseId]);

  const fetchCourseData = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId)
        .single();

      if (error) throw error;
      setCourse(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch course data",
        variant: "destructive"
      });
    }
  };

  const fetchEnrollmentStats = async () => {
    try {
      const { data: enrollments, error } = await supabase
        .from('enrollments')
        .select('status')
        .eq('course_id', courseId);

      if (error) throw error;

      const stats = enrollments?.reduce((acc, enrollment) => {
        acc.total++;
        if (enrollment.status === 'enrolled') acc.enrolled++;
        if (enrollment.status === 'completed') acc.completed++;
        if (enrollment.status === 'waitlisted') acc.waitlisted++;
        return acc;
      }, { total: 0, enrolled: 0, completed: 0, waitlisted: 0 }) || { total: 0, enrolled: 0, completed: 0, waitlisted: 0 };

      setStats(stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!course || !courseId) return;

    try {
      // First, delete all related enrollments
      const { error: enrollmentError } = await supabase
        .from('enrollments')
        .delete()
        .eq('course_id', courseId);

      if (enrollmentError) {
        console.warn('Error deleting enrollments:', enrollmentError);
        // Continue with course deletion even if enrollment deletion fails
      }

      // Delete the course
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', courseId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Course deleted successfully"
      });

      // Navigate back to My Courses page
      navigate('/my-courses');
    } catch (error) {
      console.error('Error deleting course:', error);
      toast({
        title: "Error",
        description: "Failed to delete course",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div className="p-6">Loading course management...</div>;
  }

  if (!course) {
    return <div className="p-6">Course not found</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="space-y-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{course.title}</h1>
          </div>
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            <Badge variant={course.status === 'published' ? 'default' : 'secondary'}>
              {course.status}
            </Badge>
            <Button
              variant="outline"
              onClick={() => navigate(`/course-player?courseId=${courseId}`)}
            >
              <Play className="h-4 w-4 mr-2" />
              Preview Course
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/course-builder/${courseId}`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Course
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Course
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the course
                    "{course.title}" and all associated data including enrollments.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteCourse}>
                    Delete Course
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        
        {/* Full-width course description */}
        <div className="text-muted-foreground leading-relaxed max-w-none">
          {course.description.split('\n').map((paragraph, index) => (
            paragraph.trim() ? (
              <p key={index} className="mb-2">{paragraph.trim()}</p>
            ) : (
              <br key={index} />
            )
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total Enrolled</p>
                <p className="font-semibold">{stats.enrolled}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="font-semibold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-yellow-500" />
              <div>
                <p className="text-sm text-muted-foreground">Waitlisted</p>
                <p className="font-semibold">{stats.waitlisted}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">Max Capacity</p>
                <p className="font-semibold">{course.max_participants || 'Unlimited'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="completion" className="space-y-4">
        <TabsList>
          <TabsTrigger value="completion" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Course Completion
          </TabsTrigger>
          <TabsTrigger value="attendance" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Attendance
          </TabsTrigger>
          <TabsTrigger value="communication" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Communication
          </TabsTrigger>
        </TabsList>

        <TabsContent value="completion">
          <CompletionManager courseId={courseId!} />
        </TabsContent>

        <TabsContent value="attendance">
          <AttendanceManager courseId={courseId!} />
        </TabsContent>

        <TabsContent value="communication">
          <InstructorCommunication courseId={courseId!} />
        </TabsContent>
      </Tabs>
    </div>
  );
}